import { useRef, useState } from "react";
import { Box, Flex, Switch, Text, VStack } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import {
  useEmailSettingsQuery,
  useEmailSettingsMutation,
} from "../hooks/useSettingsMutations";
import type { EmailSettingItem } from "../api/settingsApi";

export function NotificationsSection() {
  const [t] = useTranslation();
  const { data: settings, dataUpdatedAt, isLoading } = useEmailSettingsQuery();
  const { mutate } = useEmailSettingsMutation();
  // Optimistic overrides — cleared whenever React Query delivers fresh server data
  const [optimistic, setOptimistic] = useState<Record<string, boolean>>({});
  const lastSyncRef = useRef(0);

  if (dataUpdatedAt !== lastSyncRef.current) {
    lastSyncRef.current = dataUpdatedAt;
    setOptimistic({});
  }

  const handleToggle = (id: string, value: boolean) => {
    setOptimistic((prev) => ({ ...prev, [id]: value }));
    mutate(
      { [id]: value },
      { onError: () => setOptimistic((prev) => { const next = { ...prev }; delete next[id]; return next; }) },
    );
  };

  if (isLoading || !settings) {
    return null;
  }

  return (
    <VStack spacing={5} align="stretch">
      <Text fontSize="xl" fontFamily="heading" fontWeight={600} color="dune.dune">
        {t("screens.settings.emailNotifications")}
      </Text>

      <Box>
        {settings.map((setting: EmailSettingItem, idx: number) => (
          <Flex
            key={setting.id}
            justify="space-between"
            align="center"
            py={3.5}
            _first={{ pt: 0 }}
            borderBottom={idx === settings.length - 1 ? "none" : "1px solid"}
            borderColor="transparent.white.05"
            gap={4}
          >
            <Text fontSize="md" color="dune.dune">
              {setting.name}
            </Text>
            <Switch
              isChecked={optimistic[setting.id] ?? setting.enabled}
              onChange={(e) => handleToggle(setting.id, e.target.checked)}
              variant="brandSwitch"
            />
          </Flex>
        ))}
      </Box>
    </VStack>
  );
}
